import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { endpoints } from '../../utils/api';
import TaskCasePicker from './TaskCasePicker';

async function choices(list, key) {
  const rows = [];
  for (let page = 1; page <= 100; page++) {
    const result = await list({ page, limit: 100 });
    rows.push(...(Array.isArray(result[key]) ? result[key] : []));
    if (!result.pagination || page >= result.pagination.pages) return rows;
  }
  throw new Error('Too many choices to load. Please contact your administrator.');
}

export default function TaskCreateDialog({ user, onClose, onCreated }) {
  const self = user?.id || user?.userId || '';
  const [form, setForm] = useState({ title: '', description: '', assignedToId: self, caseId: '', dueDate: '', priority: 'MEDIUM' });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true), [saving, setSaving] = useState(false);
  const [error, setError] = useState(''), [warning, setWarning] = useState('');
  const sending = useRef(false);
  useEffect(() => {
    let cancelled = false;
    choices(endpoints.users.list, 'users').then(staff => {
      if (cancelled) return;
      setUsers(staff.filter(person => person.isActive && person.role !== 'CLIENT'));
    }).catch(() => { if (!cancelled) setWarning('Staff choices could not be loaded. You can still assign the task to yourself.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const change = event => setForm(previous => ({ ...previous, [event.target.name]: event.target.value }));
  async function save(event) {
    event.preventDefault();
    if (sending.current) return;
    if (!form.title.trim() || !form.assignedToId) { setError('Enter a task title and choose an assignee.'); return; }
    sending.current = true; setSaving(true); setError('');
    try {
      const task = await endpoints.tasks.create({ ...form, title: form.title.trim(), caseId: form.caseId || null, dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null });
      onCreated(task);
    } catch (failure) { setError(failure.message || 'Could not create the task. Your entries have been kept.'); }
    finally { sending.current = false; setSaving(false); }
  }
  const input = 'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 bg-white';
  const assignees = users.some(person => person.id === self) ? users : [{ ...user, id: self }, ...users];
  return <Dialog open onClose={() => { if (!saving) onClose(); }} className="relative z-50">
    <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
    <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
      <div className="flex min-h-full items-center justify-center">
        <Dialog.Panel className="w-full max-w-xl rounded-xl bg-white p-6 shadow-xl">
          <Dialog.Title className="text-xl font-semibold text-gray-900">New Task</Dialog.Title>
          <form onSubmit={save} className="mt-5 space-y-4">
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            {warning && <p role="status" className="text-sm text-amber-800">{warning}</p>}
            <label className="block text-sm font-medium">Title<input autoFocus required maxLength={300} name="title" value={form.title} onChange={change} className={input} /></label>
            <label className="block text-sm font-medium">Description<textarea name="description" maxLength={4000} rows={3} value={form.description} onChange={change} className={input} /></label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium">Assigned to<select required name="assignedToId" value={form.assignedToId} onChange={change} className={input}>
                {!self && <option value="">Choose a person</option>}
                {assignees.filter(person => person.id).map(person => <option key={person.id} value={person.id}>{person.id === self ? 'Me' : [person.firstName, person.lastName].filter(Boolean).join(' ') || person.name || person.email}</option>)}
              </select></label>
              <label className="block text-sm font-medium">Priority<select name="priority" value={form.priority} onChange={change} className={input}><option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option></select></label>
            </div>
            <TaskCasePicker onChange={caseId => setForm(previous => ({ ...previous, caseId }))} />
            <label className="block text-sm font-medium">Due date and time (optional)<input type="datetime-local" name="dueDate" value={form.dueDate} onChange={change} className={input} /></label>
            <div className="flex justify-end gap-3 border-t pt-4">
              <button type="button" disabled={saving} onClick={onClose} className="rounded-md border px-4 py-2 text-sm">Cancel</button>
              <button type="submit" disabled={saving || loading} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Creating…' : loading ? 'Loading choices…' : 'Create task'}</button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </div>
  </Dialog>;
}
